import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableToken,
  useCollectionBinding,
  useContentToken,
} from '@vibesites/runtime/react';
import { VibesitesImage, cn } from '@vibesites/ui-primitives';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { clean, hasValue } from '../utils/content';

const STAGGER = ['', 'js-d1', 'js-d2', 'js-d3'];

interface Photo {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  alt_text?: string | null;
}

export function Gallery() {
  const photos = useCollectionBinding<Photo>('gallery.images', { collection: 'photo_gallery', defaultLimit: 60 });
  const allLabel = useContentToken('gallery.all_label');
  const [category, setCategory] = useState<string>('all');
  const [open, setOpen] = useState<number | null>(null);
  const [closing, setClosing] = useState(false);
  const [dir, setDir] = useState(0);
  const closeTimer = useRef<number | undefined>(undefined);

  const items = useMemo(
    () =>
      (photos || [])
        .map((p) => ({ src: clean(p.image) || clean(p.original_url), alt: clean(p.alt_text) || clean(p.title), cat: clean(p.title) }))
        .filter((p) => p.src),
    [photos],
  );

  const categories = useMemo(() => {
    const seen = new Set<string>();
    for (const p of items) if (p.cat && p.cat.length <= 24) seen.add(p.cat);
    return [...seen];
  }, [items]);

  const shown = category === 'all' ? items : items.filter((p) => p.cat === category);

  const close = useCallback(() => {
    setClosing(true);
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setOpen(null);
      setClosing(false);
    }, 220);
  }, []);
  const step = useCallback(
    (d: number) => {
      setDir(d);
      setOpen((i) => (i == null ? i : (i + d + shown.length) % shown.length));
    },
    [shown.length],
  );
  const openAt = (i: number) => {
    window.clearTimeout(closeTimer.current);
    setClosing(false);
    setDir(0);
    setOpen(i);
  };

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (open == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, close, step]);

  const tabClass = (active: boolean) =>
    cn(
      'min-h-[44px] px-5 font-heading text-[16px] font-bold uppercase tracking-wide transition-[background-color,color,transform] duration-300 ease-out hover:-translate-y-0.5 active:scale-95',
      active ? 'bg-muted text-foreground' : 'bg-primary text-[color:var(--vibe-color-text-on-primary)] hover:bg-secondary hover:text-[color:var(--vibe-color-text-on-secondary)]',
    );

  return (
    <section className="bg-background pb-16 pt-6 md:pb-20">
      <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-10">
        {categories.length > 1 && (
          <div role="tablist" aria-label="Filter photos" className="mb-8 flex flex-wrap justify-center gap-2">
            <button type="button" role="tab" aria-selected={category === 'all'} onClick={() => setCategory('all')} className={tabClass(category === 'all')}>
              <EditableToken path="gallery.all_label" as="span">
                {allLabel || 'All'}
              </EditableToken>
            </button>
            {categories.map((c) => (
              <button key={c} type="button" role="tab" aria-selected={category === c} onClick={() => setCategory(c)} className={tabClass(category === c)}>
                {c}
              </button>
            ))}
          </div>
        )}

        <EditableCollectionBinding bindingPath="gallery.images" label="Gallery Photos">
          <ul key={category} className="grid grid-cols-2 gap-2.5 sm:gap-3 md:grid-cols-3 lg:grid-cols-4">
            {shown.map((p, i) => (
              <li key={`${p.src}-${i}`} className={`js-reveal-zoom ${STAGGER[i % 4]}`}>
                <button
                  type="button"
                  onClick={() => openAt(i)}
                  className="group relative block aspect-[11/10] w-full overflow-hidden bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  aria-label={p.alt ? `Open photo: ${p.alt}` : `Open photo ${i + 1}`}
                >
                  <VibesitesImage
                    url={p.src}
                    alt={p.alt}
                    sizes="(min-width: 1024px) 300px, (min-width: 768px) 33vw, 50vw"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                  <span aria-hidden className="pointer-events-none absolute inset-0 bg-black/0 transition-colors duration-500 group-hover:bg-black/20" />
                </button>
              </li>
            ))}
          </ul>
        </EditableCollectionBinding>
      </div>

      {open != null && shown[open] && (
        <div role="dialog" aria-modal="true" aria-label="Photo viewer" className={cn('fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4', closing ? 'js-leave' : 'js-enter-fade')} onClick={close}>
          <button type="button" onClick={close} aria-label="Close" className="group absolute right-4 top-4 inline-flex h-12 w-12 items-center justify-center text-white">
            <X className="h-8 w-8 transition-transform duration-300 group-hover:rotate-90" aria-hidden />
          </button>
          {shown.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Previous photo"
              className="group absolute left-2 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center text-white sm:left-6"
            >
              <ChevronLeft className="h-9 w-9 transition-transform duration-300 group-hover:-translate-x-1" aria-hidden />
            </button>
          )}
          <figure
            key={open}
            className={cn('max-h-full max-w-5xl', dir > 0 ? 'js-slide-from-right' : dir < 0 ? 'js-slide-from-left' : 'js-enter-zoom')}
            onClick={(e) => e.stopPropagation()}
          >
            <VibesitesImage url={shown[open].src} alt={shown[open].alt} sizes="90vw" className="max-h-[82vh] w-auto object-contain" />
            {hasValue(shown[open].alt) && (
              <figcaption className="mt-3 text-center font-body text-sm text-white">{shown[open].alt}</figcaption>
            )}
          </figure>
          {shown.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Next photo"
              className="group absolute right-2 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center text-white sm:right-6"
            >
              <ChevronRight className="h-9 w-9 transition-transform duration-300 group-hover:translate-x-1" aria-hidden />
            </button>
          )}
        </div>
      )}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Gallery',
  displayName: 'Photo Gallery',
  tokens: {
    'gallery.all_label': { label: '"All" Tab Label', type: 'text', defaultValue: 'All' },
  },
  collectionBindings: {
    'gallery.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 60,
      description: 'Photo titles become the filter tabs (e.g. Community, Apartment).',
    },
  },
};

(Gallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Gallery;
